import React from 'react';
import { HeroSection } from '../../components/HeroSection';
import { motion } from 'framer-motion';
import { Scale, Building2, Users2, Stethoscope, Briefcase, GraduationCap } from 'lucide-react';
export function SystemsPower() {
  const systems = [{
    icon: <Stethoscope className="h-8 w-8" data-id="element-855" />,
    title: 'Medical System',
    description: 'Healthcare providers pathologize fatness, deny care, and prescribe weight loss as cure-all.',
    examples: ['BMI as diagnostic tool', 'Weight-based dosing discrimination', 'Surgical denial based on size']
  }, {
    icon: <Briefcase className="h-8 w-8" data-id="element-856" />,
    title: 'Employment',
    description: 'Hiring discrimination, wage gaps, and workplace harassment based on body size.',
    examples: ['Size-based hiring bias', 'Appearance clauses', 'Lack of accommodations']
  }, {
    icon: <Building2 className="h-8 w-8" data-id="element-857" />,
    title: 'Built Environment',
    description: 'Physical spaces designed to exclude and punish fat bodies through hostile architecture.',
    examples: ['Narrow seating', 'Weight limits on furniture', 'Inaccessible public transit']
  }, {
    icon: <Scale className="h-8 w-8" data-id="element-858" />,
    title: 'Legal System',
    description: 'No federal protections against size discrimination. Fat people can be legally fired, denied housing.',
    examples: ['No ADA coverage', 'Child custody bias', 'Insurance discrimination']
  }, {
    icon: <GraduationCap className="h-8 w-8" data-id="element-859" />,
    title: 'Education',
    description: 'Schools enforce diet culture, enable bullying, and create hostile learning environments.',
    examples: ['BMI report cards', 'PE humiliation', 'Desk/chair exclusion']
  }, {
    icon: <Users2 className="h-8 w-8" data-id="element-860" />,
    title: 'Social & Cultural',
    description: 'Media representation, dating discrimination, and everyday microaggressions normalize anti-fatness.',
    examples: ['Fat villain trope', 'Dating app filters', 'Public commentary']
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-861">
      <HeroSection title="Systems, Power & Anti-Fat Bias" subtitle="Understanding how anti-fatness operates at structural, institutional, and interpersonal levels." tag="Fat Liberation & Justice" bgColor="magenta" data-id="element-862" />

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-863">
        <div className="prose prose-lg" data-id="element-864">
          <p className="text-xl text-gray-700 leading-relaxed" data-id="element-865">
            Anti-fat bias isn't just individual prejudice—it's a{' '}
            <strong className="text-brand-magenta" data-id="element-866">system of oppression</strong>{' '}
            embedded in our institutions, policies, and cultural norms.
            Understanding these systems is the first step toward dismantling
            them.
          </p>
        </div>
      </section>

      {/* Systems Grid */}
      <section className="py-16 bg-white" data-id="element-867">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-868">
          <h2 className="text-3xl font-black uppercase mb-12 text-brand-black" data-id="element-869">
            Six Interconnected Systems
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-870">
            {systems.map((system, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} viewport={{
            once: true
          }} className="border-2 border-brand-black bg-brand-cream p-6 hover:shadow-xl transition-shadow" data-id="element-871">
                <div className="text-brand-magenta mb-4" data-id="element-872">{system.icon}</div>
                <h3 className="text-xl font-black uppercase mb-3 text-brand-black" data-id="element-873">
                  {system.title}
                </h3>
                <p className="text-gray-700 mb-4 text-sm leading-relaxed" data-id="element-874">
                  {system.description}
                </p>
                <div className="space-y-2" data-id="element-875">
                  <p className="text-xs font-bold uppercase text-gray-500 mb-2" data-id="element-876">
                    Examples:
                  </p>
                  {system.examples.map((example, i) => <div key={i} className="flex items-start" data-id="element-877">
                      <span className="text-brand-magenta mr-2" data-id="element-878">•</span>
                      <span className="text-sm text-gray-600" data-id="element-879">{example}</span>
                    </div>)}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Power Analysis */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-880">
        <h2 className="text-3xl font-black uppercase mb-12 text-brand-black border-b-4 border-brand-magenta inline-block pb-2" data-id="element-881">
          Who Benefits?
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-882">
          <div className="bg-brand-magenta text-white p-8 rounded-sm" data-id="element-883">
            <h3 className="text-2xl font-black uppercase mb-6" data-id="element-884">
              The Diet Industry
            </h3>
            <p className="text-lg mb-4 opacity-90" data-id="element-885">
              $72 billion annually in the US alone
            </p>
            <ul className="space-y-3 text-sm" data-id="element-886">
              <li className="flex items-start" data-id="element-887">
                <span className="mr-2" data-id="element-888">→</span>
                <span data-id="element-889">Weight loss programs, apps, and supplements</span>
              </li>
              <li className="flex items-start" data-id="element-890">
                <span className="mr-2" data-id="element-891">→</span>
                <span data-id="element-892">Pharmaceutical companies pushing weight loss drugs</span>
              </li>
              <li className="flex items-start" data-id="element-893">
                <span className="mr-2" data-id="element-894">→</span>
                <span data-id="element-895">Bariatric surgery industry</span>
              </li>
              <li className="flex items-start" data-id="element-896">
                <span className="mr-2" data-id="element-897">→</span>
                <span data-id="element-898">"Wellness" rebranding of diet culture</span>
              </li>
            </ul>
          </div>

          <div className="bg-brand-navy text-white p-8 rounded-sm" data-id="element-899">
            <h3 className="text-2xl font-black uppercase mb-6" data-id="element-900">
              White Supremacy
            </h3>
            <p className="text-lg mb-4 opacity-90" data-id="element-901">
              Anti-fatness is rooted in racism
            </p>
            <ul className="space-y-3 text-sm" data-id="element-902">
              <li className="flex items-start" data-id="element-903">
                <span className="mr-2" data-id="element-904">→</span>
                <span data-id="element-905">Body ideals based on European beauty standards</span>
              </li>
              <li className="flex items-start" data-id="element-906">
                <span className="mr-2" data-id="element-907">→</span>
                <span data-id="element-908">Pathologization of Black and Indigenous bodies</span>
              </li>
              <li className="flex items-start" data-id="element-909">
                <span className="mr-2" data-id="element-910">→</span>
                <span data-id="element-911">Eugenics history of BMI and weight science</span>
              </li>
              <li className="flex items-start" data-id="element-912">
                <span className="mr-2" data-id="element-913">→</span>
                <span data-id="element-914">
                  Controlling Black women's bodies through "health" discourse
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Intersections */}
      <section className="py-20 bg-gray-50" data-id="element-915">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-916">
          <h2 className="text-3xl font-black uppercase mb-8 text-brand-black" data-id="element-917">
            Intersectional Impact
          </h2>
          <p className="text-lg text-gray-700 mb-8" data-id="element-918">
            Anti-fat bias doesn't exist in isolation. It compounds with other
            systems of oppression:
          </p>

          <div className="space-y-6" data-id="element-919">
            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-920">
              <h4 className="font-bold text-lg mb-2" data-id="element-921">Race + Fatness</h4>
              <p className="text-gray-700" data-id="element-922">
                Fat Black people face both anti-Blackness and anti-fatness.
                Medical racism means less pain management, more coercion into
                weight loss, higher maternal mortality.
              </p>
            </div>

            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-923">
              <h4 className="font-bold text-lg mb-2" data-id="element-924">Disability + Fatness</h4>
              <p className="text-gray-700" data-id="element-925">
                Fat disabled people are blamed for their disabilities. Mobility
                aids are denied. Fatness is treated as the "real" problem to
                solve.
              </p>
            </div>

            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-926">
              <h4 className="font-bold text-lg mb-2" data-id="element-927">
                Trans Identity + Fatness
              </h4>
              <p className="text-gray-700" data-id="element-928">
                Trans fat people face additional barriers to gender-affirming
                care. Body size is weaponized to deny hormones and surgery.
              </p>
            </div>

            <div className="bg-white border-l-4 border-brand-magenta p-6" data-id="element-929">
              <h4 className="font-bold text-lg mb-2" data-id="element-930">Class + Fatness</h4>
              <p className="text-gray-700" data-id="element-931">
                Poor fat people are blamed for their poverty. "Healthy" food is
                expensive. Weight loss is marketed as path to economic mobility.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}